import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * ChartCard — wrapper cho mọi chart/section của dashboard.
 *
 * Match `.section-card` + `.dashboard-section-title` của FE cũ:
 * cream surface + brand top accent (light variant) hoặc dark raisin
 * background (dark variant — dùng cho "Tổng Kết Vận Hành").
 */
export function ChartCard({
  title,
  subtitle,
  action,
  variant = "light",
  className,
  children,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  variant?: "light" | "dark";
  className?: string;
  children: ReactNode;
}) {
  const dark = variant === "dark";
  return (
    <div
      className={cn(
        "relative min-w-0 overflow-hidden rounded-lg border p-4 shadow-sm md:p-5",
        dark
          ? "border-transparent bg-[var(--color-raisin,#231F20)] text-white"
          : "border-border bg-surface",
        className,
      )}
    >
      {!dark ? (
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-[2px]"
          style={{ background: "var(--color-brand)" }}
        />
      ) : null}
      {(title || action) && (
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {title ? (
              <h3
                className={cn(
                  "text-base font-semibold",
                  dark ? "text-white" : "text-foreground",
                )}
              >
                {title}
              </h3>
            ) : null}
            {subtitle ? (
              <p
                className={cn(
                  "mt-0.5 text-xs",
                  dark ? "text-white/70" : "text-muted-foreground",
                )}
              >
                {subtitle}
              </p>
            ) : null}
          </div>
          {action ? <div className="min-w-0 shrink-0">{action}</div> : null}
        </div>
      )}
      <div className="min-w-0 overflow-hidden">{children}</div>
    </div>
  );
}
